import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAppStore } from "@/application/stores/app";
import { ProfileShowResource } from "@/modules/shared/authorisation/types";
import { fetchProfile, logout } from "@/modules/shared/authorisation/api";
import { UserRoleEnum } from "@/modules/shared/authorisation/enums";
import { useApp } from "@/application/composables/app";
import { AppIdentifier } from "@/application/enums";
import { useLocalStorage } from "@vueuse/core";

export const useAuthStore = defineStore("auth", () => {
  const appStore = useAppStore();
  const { appIdentifier } = useApp();

  const profile = useLocalStorage<ProfileShowResource | undefined>(
    "profile",
    undefined,
    {
      serializer: {
        read: (value: string) => (value ? JSON.parse(value) : undefined),
        write: (value: ProfileShowResource | undefined) =>
          value ? JSON.stringify(value) : "",
      },
    },
  );

  const isLoading = ref(false);
  const isProfileOutdated = ref(true);

  const isLoggedIn = computed(() => !!appStore.token);
  const isAdmin = computed(() => profile.value?.role === UserRoleEnum.Admin);
  const isSuperAdmin = computed(() => profile.value?.isSuperAdmin);

  const isLoginOptional = computed(
    () =>
      appIdentifier === AppIdentifier.TenantWebsite ||
      appIdentifier === AppIdentifier.CentralWebsite,
  );

  const isCurrentUser = (userId?: string) => profile.value?.id === userId;

  const fetch = async () => {
    isLoading.value = true;

    try {
      isProfileOutdated.value = false;
      const response = await fetchProfile();

      profile.value = response.data.data;
    } catch {
      isProfileOutdated.value = true;
    }

    isLoading.value = false;
  };

  const updateProfileIfOutdated = () => {
    if (!isProfileOutdated.value) return;
    return fetch();
  };

  const clearToken = () => {
    appStore.token = null;
    profile.value = undefined;
  };

  const logoutHandler = async () => {
    await logout();
    clearToken();
  };

  return {
    profile,
    isLoggedIn,
    isAdmin,
    isSuperAdmin,
    isLoginOptional,
    fetchProfile: fetch,
    logout: logoutHandler,
    isCurrentUser,
    clearToken,
    updateProfileIfOutdated,
    isLoading,
    token: computed(() => appStore.token),
  };
});
